import { useState, useEffect, useRef, useCallback } from 'react';
import {
  QrCode, Wifi, WifiOff, Send, Bot, User, Search,
  RefreshCw, Phone, MoreVertical, ArrowLeft, Sparkles,
  Power, Loader2, CheckCheck, Check, MessageSquare, Zap, X
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import {
  getWAStatus, getWAQR, connectWA, disconnectWA, forceSyncWA,
  getWAChats, getChatMessages, sendWAMessage, markChatRead,
  getAutopilotStatus, toggleAutopilot, suggestReply
} from '../lib/whatsapp-api';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const WS_URL = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://') + '/api/wa/ws';

function formatTime(ts) {
  if (!ts) return '';
  const date = typeof ts === 'number' ? new Date(ts * 1000) : new Date(ts);
  const now = new Date();
  const diffMs = now - date;
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffDays === 0) return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return date.toLocaleDateString([], { weekday: 'short' });
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function formatMessageTime(ts) {
  if (!ts) return '';
  const date = typeof ts === 'number' ? new Date(ts * 1000) : new Date(ts);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function jidToPhone(jid) {
  // Never expose a LID to the operator UI. If the JID is a LID
  // (`<digits>@lid`) the resolved phone comes from the backend on the
  // chat doc itself; we return an empty string here so the caller can
  // fall back to chat.name. For real-phone JIDs, return the digits.
  if (!jid) return '';
  if (jid.endsWith('@lid')) return '';
  return jid.split('@')[0] || '';
}

function chatDisplayPhone(chat) {
  // Backend already resolves LID → real phone and writes it to chat.phone
  // (empty string if not yet resolved). Prefer that. Otherwise extract
  // from the JID for real-phone JIDs.
  if (chat?.phone) return chat.phone.startsWith('+') ? chat.phone : `+${chat.phone}`;
  const raw = jidToPhone(chat?.jid);
  return raw ? `+${raw}` : '';
}

// ─── QR Connection Screen ────────────────────────────────────

function QRScreen({ qr, status, onConnect, onRefresh }) {
  const isConnecting = status === 'connecting';
  const hasQR = status === 'waiting_qr' && qr;

  return (
    <div className="flex-1 flex items-center justify-center p-8" data-testid="qr-screen">
      <div className="text-center max-w-md space-y-6">
        <div className="w-16 h-16 rounded-2xl bg-emerald-500/15 flex items-center justify-center mx-auto">
          <QrCode className="w-8 h-8 text-emerald-500" />
        </div>
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Connect WhatsApp</h2>
          <p className="text-muted-foreground mt-2 text-sm leading-relaxed">
            Link your WhatsApp account by scanning the QR code with your phone.
            Open WhatsApp, go to Linked Devices, and scan below.
          </p>
        </div>

        {hasQR ? (
          <div className="relative inline-block">
            <div className="bg-white p-4 rounded-2xl shadow-lg shadow-emerald-500/10 border border-border/50">
              <img src={qr} alt="WhatsApp QR Code" className="w-64 h-64" data-testid="qr-code-img" />
            </div>
            <div className="absolute -bottom-3 left-1/2 -translate-x-1/2">
              <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">Scan with WhatsApp</Badge>
            </div>
          </div>
        ) : isConnecting ? (
          <div className="py-12">
            <Loader2 className="w-10 h-10 text-primary animate-spin mx-auto" />
            <p className="text-sm text-muted-foreground mt-4">Starting service & generating QR code...</p>
          </div>
        ) : (
          <Button onClick={onConnect} className="gold-glow px-8 py-3" data-testid="connect-wa-btn">
            <Wifi className="w-4 h-4 mr-2" />Connect WhatsApp
          </Button>
        )}

        {hasQR && (
          <Button variant="ghost" size="sm" onClick={onRefresh} className="text-muted-foreground">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" />Refresh QR
          </Button>
        )}
      </div>
    </div>
  );
}

// ─── Chat List Item ──────────────────────────────────────────

function ChatItem({ chat, isActive, onClick }) {
  return (
    <div
      onClick={onClick}
      className={cn(
        "flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors border-b border-border/20",
        isActive ? "bg-primary/10 border-l-2 border-l-primary" : "hover:bg-muted/30"
      )}
      data-testid={`chat-item-${chat.jid}`}
    >
      <div className="w-10 h-10 rounded-full bg-emerald-500/15 flex items-center justify-center flex-shrink-0">
        <span className="text-sm font-semibold text-emerald-400">{(chat.name || chatDisplayPhone(chat) || '?')?.[0]?.toUpperCase() || '?'}</span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium truncate">{chat.name || chatDisplayPhone(chat) || ''}</p>
          <span className="text-[10px] text-muted-foreground flex-shrink-0 ml-2">{formatTime(chat.last_message_at)}</span>
        </div>
        <div className="flex items-center justify-between mt-0.5">
          <p className="text-xs text-muted-foreground truncate pr-2">{chat.last_message || 'No messages'}</p>
          {chat.unread_count > 0 && (
            <span className="flex-shrink-0 w-5 h-5 rounded-full bg-emerald-500 text-[10px] font-bold text-white flex items-center justify-center">
              {chat.unread_count > 9 ? '9+' : chat.unread_count}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Message Bubble ──────────────────────────────────────────

function MessageBubble({ msg }) {
  const isMe = msg.is_from_me;
  return (
    <div className={cn("flex mb-1.5", isMe ? "justify-end" : "justify-start")} data-testid={`msg-${msg.id}`}>
      <div className={cn(
        "max-w-[70%] px-3 py-2 rounded-lg text-sm leading-relaxed",
        isMe ? "bg-emerald-700/40 text-white rounded-br-sm" : "bg-card border border-border/50 rounded-bl-sm"
      )}>
        {msg.is_ai && <span className="text-[10px] text-primary font-medium block mb-0.5">AI Reply</span>}
        <p className="whitespace-pre-wrap break-words">{msg.content}</p>
        <div className={cn("flex items-center gap-1 mt-1", isMe ? "justify-end" : "justify-start")}>
          <span className="text-[10px] text-muted-foreground">{formatMessageTime(msg.timestamp)}</span>
          {isMe && <CheckCheck className="w-3 h-3 text-blue-400" />}
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────

export const WhatsAppWebPage = () => {
  const [status, setStatus] = useState('disconnected');
  const [qr, setQr] = useState(null);
  const [connectedPhone, setConnectedPhone] = useState(null);
  const [chats, setChats] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msgInput, setMsgInput] = useState('');
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [autopilot, setAutopilot] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);
  const pollRef = useRef(null);
  const activeChatRef = useRef(null);

  // Keep activeChatRef in sync
  useEffect(() => { activeChatRef.current = activeChat; }, [activeChat]);

  // Scroll to bottom
  const scrollToBottom = useCallback(() => {
    setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
  }, []);

  // Load initial data
  useEffect(() => {
    (async () => {
      try {
        const [statusRes, autopilotRes] = await Promise.all([getWAStatus(), getAutopilotStatus()]);
        setStatus(statusRes.data.status);
        setConnectedPhone(statusRes.data.phone);
        setAutopilot(autopilotRes.data.autopilot_enabled);
        if (statusRes.data.status === 'connected') {
          const chatsRes = await getWAChats();
          setChats(chatsRes.data);
        }
      } catch (err) {
        // Always show disconnected state with Connect button — never "starting up"
        setStatus('disconnected');
        // Production: error logged silently
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // QR polling when connecting
  useEffect(() => {
    if (status === 'connecting' || status === 'waiting_qr') {
      pollRef.current = setInterval(async () => {
        try {
          const res = await getWAQR();
          if (res.data.status === 'connected') {
            setStatus('connected');
            setConnectedPhone(res.data.phone);
            setQr(null);
            clearInterval(pollRef.current);
            const chatsRes = await getWAChats();
            setChats(chatsRes.data);
            toast.success('WhatsApp connected!');
          } else if (res.data.qr) {
            setQr(res.data.qr);
            setStatus('waiting_qr');
          }
        } catch (err) {
          // Production: error logged silently
        }
      }, 3000);
    }
    return () => clearInterval(pollRef.current);
  }, [status]);

  // WebSocket connection for real-time updates
  useEffect(() => {
    if (status !== 'connected') return;

    let ws;
    let pingInterval;
    const connect = () => {
      ws = new WebSocket(WS_URL);
      wsRef.current = ws;

      ws.onopen = () => {
        pingInterval = setInterval(() => { try { ws.send('ping'); } catch(e) { clearInterval(pingInterval); } }, 25000);
      };

      ws.onmessage = (evt) => {
        if (evt.data === 'pong') return;
        try {
          const { event, data } = JSON.parse(evt.data);

          if (event === 'message') {
            // Update messages if this chat is active
            if (activeChatRef.current?.jid === data.chat_jid) {
              setMessages(prev => {
                if (prev.find(m => m.id === data.id)) return prev;
                return [...prev, data];
              });
              scrollToBottom();
            }
            // Update chat list
            setChats(prev => {
              const idx = prev.findIndex(c => c.jid === data.chat_jid);
              if (idx >= 0) {
                const updated = [...prev];
                updated[idx] = {
                  ...updated[idx],
                  last_message: data.content?.substring(0, 200),
                  last_message_at: data.created_at || new Date().toISOString(),
                  unread_count: activeChatRef.current?.jid === data.chat_jid
                    ? updated[idx].unread_count
                    : (updated[idx].unread_count || 0) + (data.is_from_me ? 0 : 1),
                };
                // Move to top
                const [chat] = updated.splice(idx, 1);
                updated.unshift(chat);
                return updated;
              } else {
                // New chat
                return [{
                  id: data.chat_jid,
                  jid: data.chat_jid,
                  phone: jidToPhone(data.chat_jid) ? `+${jidToPhone(data.chat_jid)}` : '',
                  name: data.sender_name || jidToPhone(data.chat_jid) || '',
                  last_message: data.content?.substring(0, 200),
                  last_message_at: data.created_at || new Date().toISOString(),
                  unread_count: data.is_from_me ? 0 : 1,
                }, ...prev];
              }
            });
          }

          if (event === 'status') {
            setStatus(data.status);
            if (data.status === 'connected') {
              toast.success('WhatsApp connected!');
            }
            // Don't toast on every disconnect — it's noisy during reconnects
          }

          if (event === 'autopilot') {
            setAutopilot(data.enabled);
          }
        } catch (e) {
          // Production: error logged silently
        }
      };

      ws.onclose = () => {
        clearInterval(pingInterval);
        // Reconnect after 3s
        setTimeout(() => { if (status === 'connected') connect(); }, 3000);
      };
    };

    connect();
    return () => {
      clearInterval(pingInterval);
      if (wsRef.current) wsRef.current.close();
    };
  }, [status, scrollToBottom]);

  // Load messages when active chat changes
  useEffect(() => {
    if (!activeChat) return;
    (async () => {
      try {
        const res = await getChatMessages(activeChat.jid);
        setMessages(res.data);
        scrollToBottom();
        // Mark as read
        await markChatRead(activeChat.jid);
        setChats(prev => prev.map(c => c.jid === activeChat.jid ? { ...c, unread_count: 0 } : c));
      } catch (err) {
        // Production: error logged silently
      }
    })();
  }, [activeChat, scrollToBottom]);

  const handleConnect = async () => {
    try {
      setStatus('connecting');
      toast.info('Starting WhatsApp service, this may take a moment...');
      await connectWA();
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      if (err.response?.status === 503) {
        toast.error('WhatsApp service is not available. Please try again in a minute.');
      } else {
        toast.error('Connection failed: ' + (detail || 'Please try again'));
      }
      setStatus('disconnected');
    }
  };

  const handleDisconnect = async () => {
    try {
      await disconnectWA();
      setStatus('disconnected');
      setConnectedPhone(null);
      setChats([]);
      setActiveChat(null);
      setMessages([]);
      setQr(null);
      toast.success('Disconnected');
    } catch (err) {
      toast.error('Disconnect failed');
    }
  };

  const handleSend = async () => {
    if (!msgInput.trim() || !activeChat || sending) return;
    const text = msgInput.trim();
    setMsgInput('');
    setSending(true);
    try {
      await sendWAMessage(activeChat.jid, text);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send');
      setMsgInput(text);
    } finally {
      setSending(false);
    }
  };

  const handleSuggest = async () => {
    if (!activeChat || suggesting) return;
    setSuggesting(true);
    try {
      const lastUserMsg = [...messages].reverse().find(m => !m.is_from_me);
      if (!lastUserMsg) { toast.info('No user message to reply to'); return; }
      const res = await suggestReply(activeChat.jid, lastUserMsg.content);
      if (res.data.suggestion) {
        setMsgInput(res.data.suggestion);
        toast.success('AI suggestion ready');
      }
    } catch (err) {
      toast.error('Suggestion failed');
    } finally {
      setSuggesting(false);
    }
  };

  const handleToggleAutopilot = async (val) => {
    try {
      await toggleAutopilot(val);
      setAutopilot(val);
      toast.success(val ? 'AI Autopilot enabled' : 'AI Autopilot disabled');
    } catch (err) {
      toast.error('Toggle failed');
    }
  };

  const handleForceSync = async () => {
    try {
      toast.info('Syncing chat history...');
      const res = await forceSyncWA();
      if (res.data.status === 'needs_reconnect') {
        toast.info('Full history sync requires reconnecting. Click the power button to disconnect, then reconnect with QR scan.', { duration: 6000 });
      } else if (res.data.chats_synced > 0) {
        const chatsRes = await getWAChats();
        setChats(chatsRes.data);
        toast.success(`Synced ${res.data.chats_synced} chats`);
      } else {
        toast.info('No new chats to sync. For full history, reconnect with QR scan.', { duration: 5000 });
      }
    } catch (err) {
      toast.error('Sync failed');
    }
  };

  const filteredChats = searchQuery
    ? chats.filter(c => (c.name || c.phone || '').toLowerCase().includes(searchQuery.toLowerCase()))
    : chats;

  // Not connected — show QR screen
  if (status !== 'connected' && !loading) {
    return (
      <div className="h-full flex flex-col" data-testid="whatsapp-web-page">
        <QRScreen qr={qr} status={status} onConnect={handleConnect} onRefresh={handleConnect} />
      </div>
    );
  }

  return (
    <div className="h-full flex" data-testid="whatsapp-web-page">
      {/* Left Panel: Chat List */}
      <div className={cn("w-80 border-r border-border/50 flex flex-col bg-card/30", activeChat ? "hidden lg:flex" : "flex")} data-testid="chat-list-panel">
        {/* Header */}
        <div className="p-3 border-b border-border/50 space-y-2.5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-sm font-medium">Connected</span>
              {connectedPhone && <span className="text-xs text-muted-foreground">+{connectedPhone}</span>}
            </div>
            <div className="flex items-center gap-1">
              <TooltipProvider>
                <Tooltip><TooltipTrigger asChild>
                  <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-muted/40">
                    <Zap className={cn("w-3 h-3", autopilot ? "text-primary" : "text-muted-foreground")} />
                    <span className="text-[10px] font-medium">AI</span>
                    <Switch checked={autopilot} onCheckedChange={handleToggleAutopilot} className="scale-75" data-testid="autopilot-toggle" />
                  </div>
                </TooltipTrigger><TooltipContent>AI Autopilot {autopilot ? 'ON' : 'OFF'}</TooltipContent></Tooltip>
              </TooltipProvider>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleDisconnect} data-testid="disconnect-btn">
                <Power className="w-3.5 h-3.5 text-red-400" />
              </Button>
              <TooltipProvider>
                <Tooltip><TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleForceSync} data-testid="force-sync-btn">
                    <RefreshCw className="w-3.5 h-3.5" />
                  </Button>
                </TooltipTrigger><TooltipContent>Sync Chat History</TooltipContent></Tooltip>
              </TooltipProvider>
            </div>
          </div>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <Input className="pl-8 h-8 text-xs" placeholder="Search chats..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-testid="chat-search-input" />
          </div>
        </div>

        {/* Chat List */}
        <ScrollArea className="flex-1">
          {loading ? (
            <div className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" /></div>
          ) : filteredChats.length === 0 ? (
            <div className="p-8 text-center">
              <MessageSquare className="w-10 h-10 text-muted-foreground/30 mx-auto mb-2" />
              <p className="text-xs text-muted-foreground">{searchQuery ? 'No matches' : 'No chats yet'}</p>
            </div>
          ) : (
            filteredChats.map(chat => (
              <ChatItem key={chat.jid} chat={chat} isActive={activeChat?.jid === chat.jid} onClick={() => setActiveChat(chat)} />
            ))
          )}
        </ScrollArea>
      </div>

      {/* Right Panel: Chat Messages */}
      <div className={cn("flex-1 flex flex-col", !activeChat && "hidden lg:flex")}>
        {activeChat ? (
          <>
            {/* Chat Header */}
            <div className="h-14 border-b border-border/50 flex items-center justify-between px-4 bg-card/30">
              <div className="flex items-center gap-3">
                <Button variant="ghost" size="icon" className="lg:hidden h-8 w-8" onClick={() => setActiveChat(null)} data-testid="back-to-chats-btn">
                  <ArrowLeft className="w-4 h-4" />
                </Button>
                <div className="w-9 h-9 rounded-full bg-emerald-500/15 flex items-center justify-center">
                  <span className="text-sm font-semibold text-emerald-400">{(activeChat.name || activeChat.phone)?.[0]?.toUpperCase()}</span>
                </div>
                <div>
                  <p className="text-sm font-medium" data-testid="chat-header-name">{activeChat.name || chatDisplayPhone(activeChat) || ''}</p>
                  {chatDisplayPhone(activeChat) && (
                    <p className="text-[11px] text-muted-foreground flex items-center gap-1" data-testid="chat-header-phone"><Phone className="w-2.5 h-2.5" />{chatDisplayPhone(activeChat)}</p>
                  )}
                </div>
              </div>
              {autopilot && <Badge className="bg-primary/15 text-primary border-primary/30 text-[10px]"><Zap className="w-2.5 h-2.5 mr-1" />Autopilot</Badge>}
            </div>

            {/* Messages */}
            <ScrollArea className="flex-1 px-4 py-3" data-testid="messages-area">
              <div className="space-y-0.5 max-w-3xl mx-auto">
                {messages.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground text-sm">No messages yet</div>
                ) : (
                  messages.map(msg => <MessageBubble key={msg.id} msg={msg} />)
                )}
                <div ref={messagesEndRef} />
              </div>
            </ScrollArea>

            {/* Send Bar */}
            <div className="border-t border-border/50 p-3 bg-card/30" data-testid="send-bar">
              <div className="flex items-center gap-2 max-w-3xl mx-auto">
                <TooltipProvider>
                  <Tooltip><TooltipTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-9 w-9 flex-shrink-0" onClick={handleSuggest} disabled={suggesting} data-testid="ai-suggest-btn">
                      {suggesting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4 text-primary" />}
                    </Button>
                  </TooltipTrigger><TooltipContent>AI Suggest Reply</TooltipContent></Tooltip>
                </TooltipProvider>
                <Input
                  className="flex-1 h-9 text-sm"
                  placeholder="Type a message..."
                  value={msgInput}
                  onChange={e => setMsgInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSend()}
                  data-testid="message-input"
                />
                <Button size="icon" className="h-9 w-9 bg-emerald-600 hover:bg-emerald-700 flex-shrink-0" onClick={handleSend} disabled={!msgInput.trim() || sending} data-testid="send-message-btn">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          </>
        ) : (
          /* Empty state */
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="w-20 h-20 rounded-2xl bg-muted/30 flex items-center justify-center mx-auto mb-4">
                <MessageSquare className="w-10 h-10 text-muted-foreground/30" />
              </div>
              <p className="text-muted-foreground text-sm">Select a chat to start messaging</p>
              <p className="text-muted-foreground/60 text-xs mt-1">{chats.length} conversations</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
